import { GeographicInfoCountry } from "@/Common/Models/Localization/GeographicInfoCountry";
import { GeographicInfoState } from "@/Common/Models/Localization/GeographicInfoState";
import { ContactDirectoryClient } from "../Api/ContactDirectoryClient";
import { DirectoryEntityType } from "../Api/medfar-contactdirectory/dist/models/DirectoryEntityType";
import { ClinicNameStreetAddress } from "../Models/ClinicNameStreetAddress";
import { DirectoryPharmacy } from "../Models/DirectoryPharmacy";
import { DirectorySelectableEntity } from "../Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "../Models/IDirectoryEntity";

export class ContactDirectoryHelper {
    public static readonly supportedCountryCodes = ["CA", "US"];

    public static getSupportedCountries(): GeographicInfoCountry[] {
        return cacheStore.GetGeographicCountriesByIsoCodes(this.supportedCountryCodes);
    }

    public static getDefaultCountryAndState(): { defaultCountry: GeographicInfoCountry, defaultState: GeographicInfoState } {
        const preferredAddress = sessionStore.getCurrentEmployee()?.getActiveSite()?.getPreferredAddress();
        const countryLookup = cacheStore.GetLookupById(preferredAddress?.CountryLookupId);
        const stateLookup = cacheStore.GetLookupById(preferredAddress?.StateLookupId);
        const preferredCountry = cacheStore.LocationMapper.getGeoCountryByLookupId(countryLookup?.LookupID);
        const preferredState = cacheStore.LocationMapper.getGeoStateByLookupId(stateLookup?.LookupID);

        const isPreferredCountrySupported = ContactDirectoryHelper.supportedCountryCodes.includes(preferredCountry?.Alpha2Code);

        const defaultCountry = isPreferredCountrySupported
            ? preferredCountry
            : cacheStore.getCountryMedfarDictionariesByLookUpCode(
                cacheStore.GetCountryLookupCodeByISOCodeAlpha2(ContactDirectoryHelper.supportedCountryCodes[0])
            );

        if (isPreferredCountrySupported && preferredState)
            return { defaultCountry, defaultState: preferredState };

        const supportedStates = cacheStore.GetGeographicsStatesForCountryId(defaultCountry.Id);

        return {
            defaultCountry,
            defaultState: supportedStates.firstOrDefault()
        };
    }

    public static isMylePharmacy(model: IDirectoryEntity): boolean {
        return model instanceof DirectoryPharmacy && !!model.isMylePharmacy;
    }

    public static async getDirectoryEntity(
        contactDirectoryEntityId: string,
        contactDirectoryEntityType: DirectoryEntityType
    ): Promise<DirectorySelectableEntity<IDirectoryEntity>> {
        return await ContactDirectoryClient
            .getInstance()
            .getSelectableEntityById(
                contactDirectoryEntityType,
                contactDirectoryEntityId
            );
    }

    /**
     * Parses a full address string to separate the clinic name and street address.
     * If the address starts with a number, it is treated as the street address.
     * If the address starts with a letter, the clinic name is extracted, from the
     * beginning up to the first number, and the rest is considered the street address
     * @param fullAddress The complete address string to parse.
     */
    public static parseClinicNameStreetAddress(fullAddress?: string): ClinicNameStreetAddress | null {
        if (!fullAddress) {
            return null;
        }
        const numberRegex = /\d/;
        const firstNumberIndex = fullAddress.search(numberRegex);

        if (firstNumberIndex === 0) {
            return {
                clinicName: "",
                address: fullAddress
            };
        }

        return {
            clinicName: fullAddress.substring(0, firstNumberIndex).trim(),
            address: fullAddress.substring(firstNumberIndex).trim()
        };
    }
}
